import React, { useState, useEffect, useRef, useLayoutEffect } from 'react';
import { Bot, ArrowDown } from 'lucide-react';
import { Message, formatTime } from '../utils/chatUtils';
import TypingEffect from './TypingEffect';

// Função para processar texto com formatação básica
const formatMessageText = (text: string) => {
  // Processa texto entre asteriscos para negrito
  return text.split(/(\*[^*]+\*)/).map((part, index) => {
    if (part.startsWith('*') && part.endsWith('*')) {
      // Remove os asteriscos e aplica negrito
      const boldText = part.substring(1, part.length - 1);
      return <strong key={index}>{boldText}</strong>;
    }
    return part;
  });
};

interface ChatMessagesProps {
  messages: Message[];
  isTyping: boolean;
  messagesEndRef: React.RefObject<HTMLDivElement>;
}

const ChatMessages: React.FC<ChatMessagesProps> = ({
  messages,
  isTyping,
  messagesEndRef
}) => {
  // Initialize animatedMessages from localStorage or empty object
  const [animatedMessages, setAnimatedMessages] = useState<Record<string, boolean>>(() => {
    try {
      const saved = localStorage.getItem('chat_animated_messages');
      return saved ? JSON.parse(saved) : {};
    } catch (e) {
      console.error('Error loading animated messages from localStorage:', e);
      return {};
    }
  });
  const containerRef = useRef<HTMLDivElement>(null);
  const [isScrolling, setIsScrolling] = useState(false);
  const [userHasScrolled, setUserHasScrolled] = useState(false);
  const [isNearBottom, setIsNearBottom] = useState(true);
  
  // Save animatedMessages to localStorage whenever it changes
  useEffect(() => {
    try {
      localStorage.setItem('chat_animated_messages', JSON.stringify(animatedMessages));
    } catch (e) {
      console.error('Error saving animated messages to localStorage:', e);
    }
  }, [animatedMessages]);
  
  // Function to check if scroll is near bottom
  const checkIfNearBottom = () => {
    if (containerRef.current) {
      const container = containerRef.current;
      const isAtBottom = container.scrollHeight - container.scrollTop <= container.clientHeight + 50;
      setIsNearBottom(isAtBottom);
      return isAtBottom;
    }
    return true;
  };
  
  // Function to scroll to bottom with better reliability
  const scrollToBottom = (immediate = false, force = false) => {
    if (containerRef.current) {
      try {
        // Only auto-scroll if user hasn't scrolled up or if forced
        if (force || isNearBottom || !userHasScrolled) {
          // Set scrolling state to prevent concurrent scrolls
          setIsScrolling(true);
          
          // Direct scroll to bottom for more reliable positioning
          containerRef.current.scrollTop = containerRef.current.scrollHeight;
          
          // Reset scrolling state after a delay
          setTimeout(() => {
            setIsScrolling(false);
          }, 100);
        }
      } catch (error) {
        console.error('Error scrolling to bottom:', error);
        setIsScrolling(false);
      }
    }
  };
  
  // Use layout effect for initial scroll - this runs synchronously before browser paint
  useLayoutEffect(() => {
    // Always scroll to bottom on initial render and when messages change
    scrollToBottom(true, true); // Force scroll on initial load
    
    // Set a timeout to scroll again after a short delay (helps with dynamic content)
    const timer = setTimeout(() => {
      scrollToBottom(true, true); // Force scroll after initial render
    }, 50);
    
    return () => clearTimeout(timer);
  }, [messages]); // Only depend on messages changing
  
  // Also scroll when typing state changes
  useEffect(() => {
    if (isTyping) {
      scrollToBottom();
    }
  }, [isTyping]);
  
  // Add scroll listener to detect when user manually scrolls
  useEffect(() => {
    const container = containerRef.current;
    if (!container) return;
    
    const handleScroll = () => {
      // Only check if we're not currently programmatically scrolling
      if (!isScrolling) {
        setUserHasScrolled(true);
        checkIfNearBottom();
      }
    };
    
    container.addEventListener('scroll', handleScroll);
    return () => container.removeEventListener('scroll', handleScroll);
  }, [isScrolling]);
  
  // Periodic scroll check for long messages being typed
  useEffect(() => {
    if (isTyping) {
      // Set up interval to check and scroll during typing
      const interval = setInterval(() => {
        // Only auto-scroll if near bottom or haven't scrolled
        if (isNearBottom || !userHasScrolled) {
          scrollToBottom();
        }
      }, 500); // Check every 500ms during typing
      
      return () => clearInterval(interval);
    }
  }, [isTyping, isNearBottom, userHasScrolled]);
  
  const handleAnimationComplete = (messageId: string) => {
    setAnimatedMessages(prev => ({
      ...prev,
      [messageId]: true
    }));
    
    // Scroll to bottom after animation completes
    scrollToBottom();
  };
  
  // Function to handle typing progress
  const handleTypingProgress = () => {
    // Scroll as typing progresses, but only if near bottom
    if (isNearBottom || !userHasScrolled) {
      scrollToBottom();
    }
  };

  return (
    <div className="flex-1 chat-messages relative" ref={containerRef}>
      {/* Fixed container with auto height */}
      <div className="messages-container">
        {messages.map((msg) => (
          <div 
            key={msg.id} 
            className={`message-row ${msg.sender === 'bot' ? 'message-bot' : 'message-user'}`}
          >
            {msg.sender === 'bot' && (
              <div className="avatar-bot">
                <Bot size={16} className="text-blue-600" />
              </div>
            )}
            
            <div 
              className={`message-bubble ${msg.sender === 'bot' ? 'bot-message-bubble' : 'user-message-bubble'}`}
            >
              <div className="message-text">
                {msg.sender === 'bot' && !animatedMessages[msg.id] ? (
                  <TypingEffect 
                    text={msg.text} 
                    delay={5}
                    onComplete={() => handleAnimationComplete(msg.id)}
                    onProgress={handleTypingProgress}
                  />
                ) : (
                  formatMessageText(msg.text)
                )}
              </div>
              <div className="message-time">
                {formatTime(msg.timestamp)}
              </div>
            </div>
            
            {msg.sender === 'user' && (
              <div className="avatar-user">
                <svg width="16" height="16" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                  <path d="M12 11C14.2091 11 16 9.20914 16 7C16 4.79086 14.2091 3 12 3C9.79086 3 8 4.79086 8 7C8 9.20914 9.79086 11 12 11Z" fill="white"/>
                  <path d="M12 13C7.03228 13 3 17.0323 3 22H21C21 17.0323 16.9677 13 12 13Z" fill="white"/>
                </svg>
              </div>
            )}
          </div>
        ))}
        
        {isTyping && (
          <div className="message-row message-bot">
            <div className="avatar-bot">
              <Bot size={16} className="text-blue-600" />
            </div>
            <div className="message-bubble bot-message-bubble typing-bubble">
              <div className="typing-indicator">
                <span></span>
                <span></span>
                <span></span>
              </div>
            </div>
          </div>
        )}
      </div>
      
      {/* Scroll to bottom button - only shown when user has scrolled up and there's new content */}
      {userHasScrolled && !isNearBottom && (isTyping || messages.length > 0) && (
        <button 
          className="scroll-to-bottom-btn"
          onClick={() => scrollToBottom(true, true)}
          aria-label="Scroll to bottom"
        >
          <ArrowDown size={20} className="arrow-icon" />
        </button>
      )}
      
      <div ref={messagesEndRef} className="scroll-anchor" />
    </div>
  );
};

export default ChatMessages;
